<template>
  <div class="ask-a-question-prompt-wrap">
    <div v-if="pickerOpen" class="ask-a-question-prompt-wrap__picker">
      <MentionPicker
        ref="pickerRef"
        :list-id="listId"
        :employees="mentionResults"
        :loading="mentionLoading"
        :error="mentionError"
        @select="handleSelectMention"
        @close="dismissPicker"
        @active-change="activeOptionId = $event"
      />
    </div>
    <div
      class="ask-a-question-prompt"
      :class="{
        'ask-a-question-prompt--disabled': disabled,
        'ask-a-question-prompt--generating': generating,
      }"
    >
      <input
        ref="fileInputRef"
        type="file"
        class="ask-a-question-prompt__file-input"
        tabindex="-1"
        aria-hidden="true"
        multiple
        :accept="acceptedFileTypes"
        :disabled="disabled"
        @change="handleFileInputChange"
      />
      <div
        v-if="attachments.length"
        class="ask-a-question-prompt__attachments"
        data-testid="ask-a-question-prompt-attachments"
      >
        <AskAQuestionAttachmentCard
          v-for="(file, index) in attachments"
          :key="file.id || `${file.name}-${index}`"
          :attachment="file"
          :index="index"
          :disabled="disabled"
          @remove="handleRemoveAttachment(index)"
          @preview-image="openImagePreview"
        />
      </div>
      <p
        v-if="attachmentError"
        class="ask-a-question-prompt__error"
        role="alert"
        data-testid="ask-a-question-prompt-error"
      >
        {{ attachmentError }}
      </p>
      <AskAQuestionAttachmentImagePreview
        :open="Boolean(imagePreview)"
        :image-src="imagePreview?.src || ''"
        :image-name="imagePreview?.name || 'Image preview'"
        @close="closeImagePreview"
      />
      <div class="ask-a-question-prompt__field">
        <div
          v-if="hasTokens"
          ref="highlightRef"
          class="ask-a-question-prompt__highlight"
          aria-hidden="true"
          data-testid="ask-a-question-prompt-highlight"
        >
          <span
            v-for="segment in segments"
            :key="segment.start"
            :class="{ 'ask-a-question-prompt__mention': segment.mention }"
            :data-employee-id="segment.mention ? segment.mention.employee_id : null"
            >{{ segment.text }}</span
          >
        </div>
        <textarea
          ref="inputRef"
          class="ask-a-question-prompt__input"
          :class="{
            'ask-a-question-prompt__input--mentions': mentionsEnabled,
            'ask-a-question-prompt__input--has-tokens': hasTokens,
          }"
          aria-label="Ask a question"
          :role="mentionsEnabled ? 'combobox' : null"
          :aria-autocomplete="mentionsEnabled ? 'list' : null"
          :aria-expanded="mentionsEnabled ? String(pickerOpen) : null"
          :aria-controls="pickerOpen ? listId : null"
          :aria-activedescendant="pickerOpen ? activeOptionId : null"
          :placeholder="placeholder"
          :disabled="disabled"
          :value="inputValue"
          rows="1"
          @input="handleInput"
          @keydown="handleKeydown"
          @keydown.enter.exact.prevent="handleEnter"
          @keyup="syncCaret"
          @click="syncCaret"
          @scroll="syncHighlightScroll"
          @blur="dismissPicker"
          @paste="handlePaste"
        ></textarea>
      </div>
      <div class="ask-a-question-prompt__actions">
        <EXTooltip
          v-if="!canAddAttachments"
          placement="top"
          :delay="300"
          :title="attachLimitTooltipTitle"
        >
          <span class="ask-a-question-prompt__attach-wrap">
            <button
              type="button"
              class="ask-a-question-prompt__attach"
              :aria-label="attachAriaLabel"
              :disabled="isAttachDisabled"
              data-testid="ask-a-question-prompt-attach"
              @click="handleAttachClick"
            >
              <Icon name="ask_question_attach" size="small" class="ask-a-question-prompt__icon" />
            </button>
          </span>
        </EXTooltip>
        <span v-else class="ask-a-question-prompt__attach-wrap">
          <button
            type="button"
            class="ask-a-question-prompt__attach"
            aria-label="Attach file"
            :disabled="disabled"
            data-testid="ask-a-question-prompt-attach"
            @click="handleAttachClick"
          >
            <Icon name="ask_question_attach" size="small" class="ask-a-question-prompt__icon" />
          </button>
        </span>
        <button
          v-if="generating"
          type="button"
          class="ask-a-question-prompt__stop"
          aria-label="Stop"
          data-testid="ask-a-question-prompt-stop"
          @click="emit('stop')"
        >
          <svg
            class="ask-a-question-prompt__icon"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect x="1.5" y="1.5" width="11" height="11" rx="2" fill="currentColor" />
          </svg>
        </button>
        <button
          v-else
          type="button"
          class="ask-a-question-prompt__send"
          :class="{ 'ask-a-question-prompt__send--active': hasValue }"
          aria-label="Send"
          :disabled="disabled"
          @click="handleSubmit"
        >
          <Icon name="ask_question_send" size="small" class="ask-a-question-prompt__icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import MentionPicker from "@components/MentionPicker/MentionPicker.vue";
import AskAQuestionAttachmentCard from "./AskAQuestionAttachmentCard.vue";
import AskAQuestionAttachmentImagePreview from "./AskAQuestionAttachmentImagePreview.vue";
import {
  attachmentFromFile,
  isSupportedAttachmentFile,
  MAX_ASK_ATTACHMENTS,
  UNSUPPORTED_ATTACHMENT_MESSAGE,
} from "./resolveAttachmentPreview";
import {
  findMentionRanges,
  findMentionTrigger,
  normalizeMentions,
  segmentTextWithMentions,
} from "./mentionTokens";

const ACCEPTED_FILE_TYPES = ".pdf,.docx,image/png,image/jpeg,image/gif,image/webp";
const MENTION_SEARCH_DEBOUNCE_MS = 150;

let promptInstanceCount = 0;

const props = defineProps({
  placeholder: {
    type: String,
    default: "Ask a question",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * True while IQ is answering. The send button becomes a stop button so the
   * user can cancel the in-flight query.
   */
  generating: {
    type: Boolean,
    default: false,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  maxAttachments: {
    type: Number,
    default: MAX_ASK_ATTACHMENTS,
  },
  /**
   * Enables `@` employee mentions. Called with the text typed after `@`; resolves
   * to rows of `{ employee_id, display_name, position_title, avatar }`.
   */
  mentionSource: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits([
  "submit",
  "stop",
  "attach",
  "remove-attachment",
  "paste-attachment",
  "add-attachment",
  "attachment-limit-reached",
]);

const listId = `ask-a-question-mentions-${++promptInstanceCount}`;
const inputValue = ref("");
const inputRef = ref(null);
const highlightRef = ref(null);
const pickerRef = ref(null);
const caretPos = ref(0);
const pickedMentions = ref([]);
const dismissedKey = ref("");
const activeOptionId = ref(null);
const mentionResults = ref([]);
const mentionLoading = ref(false);
const mentionError = ref(false);
let searchTimer = null;
let searchRequestId = 0;
const fileInputRef = ref(null);
const imagePreview = ref(null);
const attachmentError = ref("");
const acceptedFileTypes = ACCEPTED_FILE_TYPES;

const mentionsEnabled = computed(() => typeof props.mentionSource === "function");

const activeRanges = computed(() =>
  mentionsEnabled.value ? findMentionRanges(inputValue.value, pickedMentions.value) : []
);

const hasTokens = computed(() => activeRanges.value.length > 0);

const segments = computed(() => segmentTextWithMentions(inputValue.value, pickedMentions.value));

const currentTrigger = computed(() =>
  mentionsEnabled.value && !props.disabled
    ? findMentionTrigger(inputValue.value, caretPos.value, pickedMentions.value)
    : null
);

const triggerKey = computed(() =>
  currentTrigger.value ? `${currentTrigger.value.start}:${currentTrigger.value.query}` : ""
);

const pickerOpen = computed(() => Boolean(triggerKey.value) && triggerKey.value !== dismissedKey.value);

const hasValue = computed(() => inputValue.value.trim().length > 0 || props.attachments.length > 0);

const canAddAttachments = computed(() => props.attachments.length < props.maxAttachments);

const isAttachDisabled = computed(() => props.disabled || !canAddAttachments.value);

const attachAriaLabel = computed(() =>
  canAddAttachments.value ? "Attach file" : `Attach file (maximum ${props.maxAttachments} files)`
);

const attachLimitTooltipTitle = computed(
  () => `Maximum ${props.maxAttachments} attachments can be uploaded.`
);

const openImagePreview = (payload) => {
  if (!payload?.src) {
    return;
  }
  imagePreview.value = payload;
};

const closeImagePreview = () => {
  imagePreview.value = null;
};

const remainingAttachmentSlots = () => Math.max(0, props.maxAttachments - props.attachments.length);

const tryEmitAttachment = (file) => {
  if (!canAddAttachments.value) {
    emit("attachment-limit-reached");
    return false;
  }
  if (!isSupportedAttachmentFile(file)) {
    attachmentError.value = UNSUPPORTED_ATTACHMENT_MESSAGE;
    return false;
  }
  emit("add-attachment", attachmentFromFile(file));
  return true;
};

const handleRemoveAttachment = (index) => {
  attachmentError.value = "";
  emit("remove-attachment", index);
};

const handleSubmit = () => {
  if (props.disabled) {
    return;
  }
  attachmentError.value = "";
  const mentions = normalizeMentions(activeRanges.value.map((range) => range.mention));
  if (mentions.length > 0) {
    emit("submit", inputValue.value, mentions);
  } else {
    emit("submit", inputValue.value);
  }
  inputValue.value = "";
  pickedMentions.value = [];
  caretPos.value = 0;
};

const syncHighlightScroll = () => {
  if (highlightRef.value && inputRef.value) {
    highlightRef.value.scrollTop = inputRef.value.scrollTop;
  }
};

const syncCaret = () => {
  if (inputRef.value) {
    caretPos.value = inputRef.value.selectionStart ?? inputValue.value.length;
  }
};

const handleInput = (event) => {
  inputValue.value = event.target.value;
  caretPos.value = event.target.selectionStart ?? inputValue.value.length;
  nextTick(syncHighlightScroll);
};

const placeCaret = async (position) => {
  caretPos.value = position;
  await nextTick();
  if (inputRef.value) {
    inputRef.value.focus();
    inputRef.value.setSelectionRange(position, position);
  }
};

const dismissPicker = () => {
  if (pickerOpen.value) {
    dismissedKey.value = triggerKey.value;
  }
};

const handleSelectMention = (employee) => {
  const trigger = currentTrigger.value;
  const label = String(employee?.display_name || "").trim();
  if (!trigger || !label) {
    return;
  }
  const value = inputValue.value;
  const token = `@${label}`;
  const suffix = value.slice(trigger.end);
  inputValue.value = `${value.slice(0, trigger.start)}${token}${
    suffix.startsWith(" ") ? "" : " "
  }${suffix}`;
  pickedMentions.value = normalizeMentions([
    ...pickedMentions.value,
    { employee_id: employee.employee_id, label },
  ]);
  placeCaret(trigger.start + token.length + 1);
};

const handleBackspace = (event) => {
  const input = inputRef.value;
  if (!input || input.selectionStart !== input.selectionEnd) {
    return;
  }
  const range = activeRanges.value.find((item) => item.end === input.selectionStart);
  if (!range) {
    return;
  }
  event.preventDefault();
  inputValue.value = inputValue.value.slice(0, range.start) + inputValue.value.slice(range.end);
  placeCaret(range.start);
};

const handleKeydown = (event) => {
  if (!mentionsEnabled.value || props.disabled) {
    return;
  }
  if (event.key === "Backspace") {
    handleBackspace(event);
    return;
  }
  if (pickerOpen.value && event.key !== "Enter") {
    pickerRef.value?.handleKeydown(event);
  }
};

const handleEnter = () => {
  if (pickerOpen.value) {
    if (mentionLoading.value || pickerRef.value?.selectActive()) {
      return;
    }
  }
  handleSubmit();
};

watch(triggerKey, (key) => {
  if (!key) {
    dismissedKey.value = "";
  }
});

watch(
  () => (pickerOpen.value ? triggerKey.value : ""),
  (key) => {
    clearTimeout(searchTimer);
    searchRequestId += 1;
    if (!key) {
      mentionResults.value = [];
      mentionLoading.value = false;
      mentionError.value = false;
      return;
    }
    const requestId = searchRequestId;
    const query = currentTrigger.value.query;
    mentionLoading.value = true;
    mentionError.value = false;
    searchTimer = setTimeout(async () => {
      try {
        const rows = await props.mentionSource(query);
        if (requestId === searchRequestId) {
          mentionResults.value = Array.isArray(rows) ? rows : [];
        }
      } catch {
        if (requestId === searchRequestId) {
          mentionResults.value = [];
          mentionError.value = true;
        }
      } finally {
        if (requestId === searchRequestId) {
          mentionLoading.value = false;
        }
      }
    }, MENTION_SEARCH_DEBOUNCE_MS);
  }
);

onBeforeUnmount(() => {
  clearTimeout(searchTimer);
  searchRequestId += 1;
});

const openFilePicker = () => {
  if (props.disabled || !fileInputRef.value) {
    return;
  }
  fileInputRef.value.value = "";
  fileInputRef.value.click();
};

const handleAttachClick = () => {
  if (props.disabled || !canAddAttachments.value) {
    if (!canAddAttachments.value) {
      emit("attachment-limit-reached");
    }
    return;
  }
  emit("attach");
  openFilePicker();
};

const handleFileInputChange = (event) => {
  attachmentError.value = "";
  let slots = remainingAttachmentSlots();
  if (!slots) {
    emit("attachment-limit-reached");
    return;
  }
  const files = Array.from(event.target.files || []);
  files.forEach((file) => {
    if (!slots) {
      emit("attachment-limit-reached");
      return;
    }
    if (tryEmitAttachment(file)) {
      slots -= 1;
    }
  });
};

const handlePaste = (event) => {
  if (props.disabled) {
    return;
  }

  const items = event.clipboardData?.items ?? [];
  if (!canAddAttachments.value) {
    emit("attachment-limit-reached");
    return;
  }

  const imageItem = Array.from(items).find((item) => item.type.startsWith("image/"));
  if (!imageItem) {
    return;
  }

  event.preventDefault();
  const file = imageItem.getAsFile();
  if (!file) {
    return;
  }
  attachmentError.value = "";
  if (!isSupportedAttachmentFile({ type: imageItem.type })) {
    attachmentError.value = UNSUPPORTED_ATTACHMENT_MESSAGE;
    return;
  }

  const extension = imageItem.type.split("/")[1]?.toUpperCase() ?? "IMG";
  const name = `Pasted image ${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.${extension.toLowerCase()}`;

  emit("paste-attachment", { ...attachmentFromFile(file), name, extension });
};
</script>

<style scoped>
.ask-a-question-prompt-wrap {
  position: relative;
  width: 100%;
}

.ask-a-question-prompt-wrap__picker {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10;
  max-height: min(336px, calc(100vh - 240px));
  overflow-y: auto;
}

.ask-a-question-prompt {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-height: 140px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

.ask-a-question-prompt:focus-within {
  border-color: #6366f1;
}

.ask-a-question-prompt--disabled {
  background: #f9fafb;
  border-color: #e5e8ee;
}

.ask-a-question-prompt--disabled .ask-a-question-prompt__input::placeholder {
  color: #98a2af;
}

.ask-a-question-prompt--disabled .ask-a-question-prompt__actions {
  opacity: 0.4;
}

.ask-a-question-prompt--generating .ask-a-question-prompt__actions {
  opacity: 1;
}

.ask-a-question-prompt__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ask-a-question-prompt__attachments {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.ask-a-question-prompt__error {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #b42318;
}

.ask-a-question-prompt__field {
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
}

.ask-a-question-prompt__highlight {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: normal;
  color: #111827;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  pointer-events: none;
}

.ask-a-question-prompt__highlight::after {
  content: "\200b";
}

.ask-a-question-prompt__mention {
  color: #4f46e5;
  -webkit-text-stroke: 0.5px currentColor;
}

.ask-a-question-prompt__input {
  position: relative;
  flex: 1;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: normal;
  color: #111827;
}

.ask-a-question-prompt__input::placeholder {
  color: #6b7280;
}

.ask-a-question-prompt__input--mentions {
  scrollbar-width: none;
}

.ask-a-question-prompt__input--mentions::-webkit-scrollbar {
  display: none;
}

.ask-a-question-prompt__input--has-tokens {
  color: transparent;
  caret-color: #111827;
}

.ask-a-question-prompt__input:disabled {
  cursor: not-allowed;
}

.ask-a-question-prompt__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.ask-a-question-prompt__attach-wrap {
  display: inline-flex;
}

.ask-a-question-prompt__attach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
}

.ask-a-question-prompt__send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: #f3f4f6;
  border: none;
  border-radius: 16px;
  color: #6b7280;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}

.ask-a-question-prompt__send--active {
  background: #6366f1;
  color: #ffffff;
}

.ask-a-question-prompt__stop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: #6366f1;
  border: none;
  border-radius: 16px;
  color: #ffffff;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}

.ask-a-question-prompt__stop:hover {
  background: #4f46e5;
}

.ask-a-question-prompt__attach:disabled,
.ask-a-question-prompt__send:disabled {
  cursor: not-allowed;
}

.ask-a-question-prompt__attach:focus-visible,
.ask-a-question-prompt__send:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.ask-a-question-prompt__stop:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.ask-a-question-prompt__icon {
  display: block;
}
</style>
